'use client';

import { c as _c } from "react/compiler-runtime";
import { useMemo } from 'react';
import { Sparkles } from 'lucide-react';
import { useI18n } from '~/features/i18n';
import { useActivityAiActiveChat } from '../useActivityAiActiveChat';
import { useActivityAiChatStore } from '../activity-ai-chat-store';
import { RecentChatsList } from '../RecentChatsList';
import { AI_SEARCH_EVENTS, useAiSearchAnalytics } from '../ai-search-analytics';

/**
 * Mobile-only AI landing surface shown on `mode=ai` before any chat exists.
 * Mirrors the desktop chat panel's empty-state affordances: a start-a-chat CTA
 * and a recent-chats section so users can resume a previous thread without going
 * through the history drawer.
 */
export const ActivityAiMobileLanding: React.FC = () => {
  const $ = _c(24);
  const i18n = useI18n();
  const analytics = useAiSearchAnalytics();
  const aiChat = useActivityAiActiveChat();
  const threads = useActivityAiChatStore(_temp);
  let t0;
  if ($[0] !== threads) {
    t0 = Object.values(threads).sort(_temp2);
    $[0] = threads;
    $[1] = t0;
  } else {
    t0 = $[1];
  }
  const sortedThreads = t0;
  let t1;
  if ($[2] !== aiChat || $[3] !== analytics) {
    t1 = threadId => {
      analytics[AI_SEARCH_EVENTS.CHAT_RESUMED]({
        session_id: threadId,
        source: "recent_list"
      });
      aiChat.openThread(threadId);
    };
    $[2] = aiChat;
    $[3] = analytics;
    $[4] = t1;
  } else {
    t1 = $[4];
  }
  const handleResumeThread = t1;
  let t2;
  if ($[5] === Symbol.for("react.memo_cache_sentinel")) {
    t2 = <span className="flex size-11 items-center justify-center rounded-full bg-blue-900/10 text-blue-900" aria-hidden={true}><Sparkles className="size-5" strokeWidth={2.25} /></span>;
    $[5] = t2;
  } else {
    t2 = $[5];
  }
  let t3;
  if ($[6] !== i18n) {
    t3 = i18n._("activity.search.ai.landing.intro.title", undefined, {
      message: "Zoek je teambuilding, Funkey doet de rest!"
    });
    $[6] = i18n;
    $[7] = t3;
  } else {
    t3 = $[7];
  }
  let t4;
  if ($[8] !== t3) {
    t4 = <h2 className="text-lg font-bold leading-normal text-blue-900">{t3}</h2>;
    $[8] = t3;
    $[9] = t4;
  } else {
    t4 = $[9];
  }
  let t5;
  if ($[10] !== i18n) {
    t5 = i18n._("activity.search.ai.landing.intro.example", undefined, {
      message: "bv. 100 personen in Brugge, max \u20AC100 p.p."
    });
    $[10] = i18n;
    $[11] = t5;
  } else {
    t5 = $[11];
  }
  let t6;
  if ($[12] !== t5) {
    t6 = <p className="text-sm text-stone-500">{t5}</p>;
    $[12] = t5;
    $[13] = t6;
  } else {
    t6 = $[13];
  }
  let t7;
  if ($[14] !== t4 || $[15] !== t6) {
    t7 = <button type="button" onClick={_temp3} className="flex flex-col items-center gap-3 rounded-3xl bg-white px-6 py-7 text-center shadow-sm ring-1 ring-stone-200/70 transition-colors hover:ring-secondary/40">{t2}{t4}{t6}</button>;
    $[14] = t4;
    $[15] = t6;
    $[16] = t7;
  } else {
    t7 = $[16];
  }
  let t8;
  if ($[17] !== handleResumeThread || $[18] !== i18n || $[19] !== sortedThreads) {
    t8 = sortedThreads.length > 0 ? <div className="flex flex-col gap-2"><span className="px-1 text-xs font-semibold text-stone-600">{i18n._("activity.search.ai.recentChats", undefined, {
          message: "Recent chats"
        })}</span><RecentChatsList threads={sortedThreads} onSelect={handleResumeThread} limit={4} variant="drawer" /></div> : null;
    $[17] = handleResumeThread;
    $[18] = i18n;
    $[19] = sortedThreads;
    $[20] = t8;
  } else {
    t8 = $[20];
  }
  let t9;
  if ($[21] !== t7 || $[22] !== t8) {
    t9 = <div className="flex flex-col gap-4 px-4 pb-4 pt-4 lg:hidden">{t7}{t8}</div>;
    $[21] = t7;
    $[22] = t8;
    $[23] = t9;
  } else {
    t9 = $[23];
  }
  return t9;
};
function _temp(s) {
  return s.threads;
}
function _temp2(a, b) {
  return b.createdAt - a.createdAt;
}
function _temp3() {
  return document.getElementById("activity-ai-mobile-landing-prompt")?.focus();
}